import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, CircleUserRound, KeyRound, LogOut } from 'lucide-react';
import { ChangePasswordDialog } from '@/features/auth/components/ChangePasswordDialog';
import { useLogout } from '@/features/auth/hooks/useLogout';
import type { AuthUser } from '@/types/auth';
import { cn } from '@/utils/cn';
import { USER_TYPE_LABEL } from '@/utils/labels';

const menuItems = (menu: HTMLElement | null): HTMLElement[] => [
  ...(menu?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []),
];

const ITEM_CLASS =
  'flex w-full items-center gap-2.5 px-4 py-2 text-left text-sm text-ink outline-none hover:bg-canvas focus-visible:bg-canvas';

/** The signed-in user's name in the header; opens Change Password and Logout. */
export function UserMenu({ user }: { user: AuthUser }) {
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // While open: focus the first item, and close on a press anywhere outside.
  useEffect(() => {
    if (!open) return;
    menuItems(menuRef.current).at(0)?.focus();
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = menuItems(menuRef.current);
    const index = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (i: number) => items.at(i % items.length)?.focus();
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusAt(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusAt(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusAt(-1);
        break;
      case 'Escape':
        event.preventDefault();
        close();
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        className="flex items-center gap-2 rounded-md px-2 py-1 text-right hover:bg-white/10"
      >
        <CircleUserRound className="size-6 sm:hidden" aria-hidden />
        <span className="sr-only leading-tight sm:not-sr-only">
          <span className="block text-sm font-medium">{user.name}</span>
          <span className="block text-xs text-white/80">{USER_TYPE_LABEL[user.userType]}</span>
        </span>
        <ChevronDown
          className={cn('size-4 shrink-0 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label="Account"
          onKeyDown={onMenuKeyDown}
          className="absolute top-full right-0 z-30 mt-2 w-52 rounded-lg bg-white py-1 shadow-card ring-1 ring-table-head"
        >
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={ITEM_CLASS}
            onClick={() => {
              close();
              setChangingPassword(true);
            }}
          >
            <KeyRound className="size-4 text-muted" aria-hidden />
            Change Password
          </button>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={ITEM_CLASS}
            onClick={() => {
              setOpen(false);
              logout();
            }}
          >
            <LogOut className="size-4 text-muted" aria-hidden />
            Logout
          </button>
        </div>
      )}

      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}
    </div>
  );
}
